import type {
  Category,
  ExpirySource,
  Location,
  OrderSource,
  ShoppingItemStatus,
  ShoppingReason,
} from '../events/types.js';

export type { ShoppingItemStatus, ShoppingReason, OrderSource };

/**
 * Projected entity types — derived from event log (ADR-013).
 *
 * IndexedDB stores both:
 *   - events table (source of truth, append-only)
 *   - projected entity tables (cache; rebuildable from events at any time)
 */

export type ItemStatus = 'active' | 'all_consumed' | 'all_expired' | 'discarded';

export interface Batch {
  id: string;
  /**
   * 庫存量，**用 base unit 計**（v0.3 起）。
   * 例：1 盒蛋 10 顆 → quantity: 10, unit: '顆'
   *     1 瓶 900ml → quantity: 900, unit: 'ml'
   *     1 瓶汽水 → quantity: 1, unit: '瓶'（沒 packageSize 時直接用 packaging）
   */
  quantity: number;
  unit: string;
  /** v0.3：每包裝裝多少 base unit（例：10 顆/盒）。沒有時表示沒分包裝 / packaging 即 base。 */
  packageSize?: number;
  /** v0.3：包裝單位（例：'盒' / '瓶' / '罐'）。 */
  packageUnit?: string;
  addedAt: number;
  expiresAt: number | null;
  expirySource: ExpirySource;
  openedAt: number | null;
  productId?: string;
}

export interface Item {
  id: string;
  name: string;
  normalizedName: string;
  category: Category;
  location: Location;
  status: ItemStatus;
  batches: Batch[];
  brand?: string;
  notes?: string;
  /**
   * v0.2 常備需求量（base unit，跟 batch.quantity 同單位）。
   * 0 / undefined → 不是常備品，不出現在建議補貨。
   * > 0 → 當總庫存 < minQuantity 時建議補貨。
   */
  minQuantity?: number;
}

// ─────────────── Pantry Knowledge ───────────────

export interface PantryKnowledge {
  id: string;
  name: string;
  aliases: string[];
  category: Category;
  defaultLocation: Location;
  defaultExpiryDays: number;
  openedExpiryDays?: number;
  defaultUnit: string;
  similarItems?: string[];
  commonRecipes?: string[];
  /**
   * 比價基準單位（v0.2 G-full）。例 'ml' / 'g' / '顆' / '把' / '包'。
   * normalize 規則：ml/g 換算 per 100；其他 per 1（見 domain/normalize-price.ts）。
   * 可省略，省略時 ShoppingItem 仍可自填 packageContentUnit 啟用 normalize。
   */
  compareBaseUnit?: string;
  /**
   * v0.3 常見包裝規格量（搭配 compareBaseUnit）。例：
   *   鮮乳 → [300, 900, 1857, 2000] (ml)
   *   蛋   → [6, 8, 10, 15, 30] (顆)
   *   米   → [1000, 2000, 5000] (g)
   * UI 把每個值 render 成 chip，使用者一鍵套用 packageSize + packageContentUnit。
   * 不適用（蔬果論顆/把、肉海鮮論斤）的品項可省略。
   */
  commonPackageSizes?: number[];
}

// ─────────────── Shopping (v0.1.1 minimal events; v0.2 full UX) ───────────────

export interface ShoppingItem {
  id: string;
  name: string;
  quantity?: number;
  unit?: string;
  reason: ShoppingReason;
  relatedItemId?: string;
  subscriptionId?: string;
  status: ShoppingItemStatus;
  orderedAt?: number;
  /** v0.4：實際出貨時間（markInTransit 時填）。 */
  inTransitAt?: number;
  expectedArrival?: number;
  source?: OrderSource;
  /** v0.2 G-full：具體店名（自由字串，例「家樂福重慶店」「全聯」「Costco 北投店」）。 */
  sourceName?: string;
  unitPrice?: number;
  /** v0.2 G-full：包裝內含量（例：900、10）。搭配 packageContentUnit 算 normalized price。 */
  packageSize?: number;
  /** v0.2 G-full：包裝基本單位（例：'ml'、'g'、'顆'）。 */
  packageContentUnit?: string;
  capturedPhoto?: string;
  receivedAt?: number;
  addedAt: number;
}

// ─────────────── Subscription (v0.2 启用，schema 預留) ───────────────

export interface Subscription {
  id: string;
  knowledgeId: string;
  defaultName: string;
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'custom';
  customDays?: number;
  nextDelivery: number;
  source: string;
  active: boolean;
  defaultQuantity: number;
  defaultUnit: string;
  leadDays: number;
}

// ─────────────── User Settings ───────────────

export interface UserSettings {
  id: 'singleton';
  language: 'zh-TW' | 'en' | 'ja' | 'ko';
  notificationsEnabled: boolean;
  notificationTime: string;
  expiryWarningDays: number;
  knownLocations: Location[];

  syncEnabled: boolean;
  syncProvider?: 'icloud' | 'google_drive' | 'local_network';

  familyId?: string;
  showActorInUI: boolean;

  cloudLlmEnabled: boolean;
  llmProvider?: 'local' | 'claude' | 'openai' | 'gemini';

  offLookupEnabled: boolean;

  /** v0.2 採買 trip context：當前正在計畫的採買來源，自動套到加入清單的新項目。 */
  activeShoppingSource?: string;
  activeShoppingSourceName?: string;

  /** v0.5.4 主題模式（依 spec 0.5.3 dark mode 規範）：'system' = 跟 OS appearance；'light' / 'dark' = 強制覆寫。 */
  theme?: 'system' | 'light' | 'dark';
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  id: 'singleton',
  language: 'zh-TW',
  notificationsEnabled: true,
  notificationTime: '09:00',
  expiryWarningDays: 3,
  knownLocations: ['fridge', 'freezer', 'pantry', 'other'],
  syncEnabled: false,
  showActorInUI: false,
  cloudLlmEnabled: false,
  offLookupEnabled: false,
  theme: 'system',
};
